import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery } from './axiosBaseQuery.js'

/**
 * The one api. Every module injects onto it; no module creates its own.
 *
 * `endpoints` is empty on purpose — a module's endpoints belong in that module's
 * `api/` folder, next to its types, so a screen is one folder and moving it moves
 * everything it needs.
 */

/**
 * Every cache tag in the application, declared once.
 *
 * A tag that only exists at the call site is a tag nobody can find when a mutation
 * needs to invalidate it, and the result is a desk that shows yesterday's answer
 * after somebody changed it today.
 */
export const TAGS = [
  'Session',
  /** A password reset link's own validity (1.11), read once and never cached. */
  'ResetLink',
  'MyAccount',
  'Permissions',
  'Dashboard',
  'Provider',
  'Booking',
  'Payment',
  'Refund',
  'Class',
  'Enrolment',
  'Event',
  'City',
  'Category',
  /** The curated specialty list within a category (9.4's "Specialties" sheet). */
  'Specialty',
  'Role',
  'TeamMember',
  'Setting',
  'HelpArticle',
  'Quote',
  'LegalDocument',
  'SupportMessage',
  /** What members reported (App Store guideline 1.2, 2026-10-02). */
  'ContentReport',
  /** Who asked for the app's download link (client, 2026-10-02). */
  'AppRequest',
  'AuditEntry',
  /** The activity log: everything anybody did (client, 2026-10-03). Read-only. */
  'ActivityEntry',
  /**
   * The members table. Declared here from the beginning and used by nothing
   * until 2026-09-21, because the screen it belongs to was the one the desk
   * did not have — four endpoints, a tag, and no caller. The client asked for
   * the screen; this is what it tags.
   */
  'User',
  /**
   * The professional's own sign-up form, which is not a desk screen at all.
   *
   * This build serves three audiences on three paths: the client's team at the root,
   * a professional claiming an invitation at `/provider/set-password`, and a guest
   * instructor at `/teach`. They share a component layer, an HTTP client and a store;
   * they do not share a session or a rail.
   */
  'Onboarding',
  'Teaching',
] as const

export type Tag = (typeof TAGS)[number]

export const rootApi = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQuery(),
  tagTypes: TAGS,
  /** Kept for a minute after the last subscriber, so moving between screens and
   *  back does not re-fetch a table somebody is still reading. */
  keepUnusedDataFor: 60,
  refetchOnReconnect: true,
  /**
   * Coming back to the tab refreshes what is on it.
   *
   * A desk is left open. Somebody opens Bookings, goes to answer the phone,
   * comes back forty minutes later and acts on a list that stopped being
   * true before the call started — a booking cancelled since, a payment
   * refunded since, a figure from another morning. Nothing on the screen
   * said it was old, because nothing knew.
   *
   * Safe only because a refetch can no longer overwrite a half-filled form:
   * both editors and the profile sheet keep unsaved typing over anything the
   * server sends back. See `lib/useUnsaved.ts` — that was a live bug before
   * this line existed, because attaching a material refetched the class.
   */
  refetchOnFocus: true,

  endpoints: () => ({}),
})

/**
 * `{ type, id: 'LIST' }` plus one tag per row.
 *
 * The shape every list uses, written once: a mutation that changes one row
 * invalidates that row, and one that adds or removes a row invalidates the list.
 * Doing it by hand at each endpoint is how a table stops updating.
 */
export function listTags<T extends { id: string }>(type: Tag, rows: readonly T[] | undefined) {
  return rows
    ? [...rows.map((r) => ({ type, id: r.id })), { type, id: 'LIST' as const }]
    : [{ type, id: 'LIST' as const }]
}
